/**
 * Janus Agent (DATA agent) chat — ephemeral UI state for the floating
 * "Agente JanusOS" widget.
 *
 * Mirrors `useSupportAssistantStore`: no persisted history, the drawer
 * opens empty on every reload, and the audit trail lives server-side.
 * The streaming + rich-rendering pipeline is the portal data agent
 * (`agentApi.chatStream` → `/api/v1/agent/chat/stream`), so each assistant
 * turn additionally carries the agent's tool-call trace (`steps`).
 *
 * `sessionId` is keyed to the browser TAB (sessionStorage) so the user can
 * close + reopen the drawer mid-conversation without losing correlation.
 * Reload → new sessionId. "Nova conversa" forces a new sessionId.
 */

import { create } from 'zustand';
import type { AgentToolStep } from '../../modules/agent/services/agentApi';

export type JanusAgentMessageRole = 'user' | 'assistant';

export interface JanusAgentMessage {
  id: string;
  role: JanusAgentMessageRole;
  content: string;
  isStreaming?: boolean;
  /** Tool-call steps the data agent performed for this assistant turn. */
  steps?: AgentToolStep[];
  /**
   * Set when the data agent (or a tool step) signalled that this question
   * should be handed off to the Support Assistant. Drives the inline
   * "Continuar no Assistente JanusOS →" affordance on the bubble.
   */
  handoff?: boolean;
  createdAt: number;
}

const SESSION_KEY = 'janus.dataAgent.sessionId';

function cryptoRandomId(): string {
  try {
    return (crypto as Crypto).randomUUID();
  } catch {
    return 'jag-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}

function ensureSessionId(): string {
  if (typeof window === 'undefined') return cryptoRandomId();
  const existing = window.sessionStorage.getItem(SESSION_KEY);
  if (existing) return existing;
  const fresh = cryptoRandomId();
  window.sessionStorage.setItem(SESSION_KEY, fresh);
  return fresh;
}

interface JanusAgentState {
  isOpen: boolean;
  sessionId: string;
  messages: JanusAgentMessage[];
  isSending: boolean;
  errorMessage: string | null;
  /** Last thing the user typed — forwarded to the Support Assistant on hand-off. */
  lastUserMessage: string | null;

  open: () => void;
  close: () => void;
  toggle: () => void;

  /** Start a brand-new session (clears messages + rotates sessionId). */
  resetConversation: () => void;

  /** Optimistically push the user's message; returns its id. */
  appendUserMessage: (content: string) => string;

  /** Push a placeholder assistant message (used for streaming). */
  appendAssistantPlaceholder: () => string;

  /** Patch the assistant message identified by `id`. */
  patchAssistantMessage: (id: string, patch: Partial<JanusAgentMessage>) => void;

  /** Append a streamed token chunk to the assistant message. */
  appendAssistantChunk: (id: string, chunk: string) => void;

  /** Append a tool-call step to the assistant message. */
  appendToolStep: (id: string, step: AgentToolStep) => void;

  setSending: (sending: boolean) => void;
  setError: (msg: string | null) => void;
}

export const useJanusAgentStore = create<JanusAgentState>((set) => ({
  isOpen: false,
  sessionId: ensureSessionId(),
  messages: [],
  isSending: false,
  errorMessage: null,
  lastUserMessage: null,

  open: () => set({ isOpen: true, errorMessage: null }),
  close: () => set({ isOpen: false }),
  toggle: () => set((s) => ({ isOpen: !s.isOpen })),

  resetConversation: () => {
    const next = cryptoRandomId();
    try {
      window.sessionStorage.setItem(SESSION_KEY, next);
    } catch {
      /* no-op */
    }
    set({
      sessionId: next,
      messages: [],
      errorMessage: null,
      isSending: false,
      lastUserMessage: null,
    });
  },

  appendUserMessage: (content) => {
    const id = cryptoRandomId();
    set((s) => ({
      lastUserMessage: content,
      messages: [...s.messages, { id, role: 'user', content, createdAt: Date.now() }],
    }));
    return id;
  },

  appendAssistantPlaceholder: () => {
    const id = cryptoRandomId();
    set((s) => ({
      messages: [
        ...s.messages,
        { id, role: 'assistant', content: '', isStreaming: true, steps: [], createdAt: Date.now() },
      ],
    }));
    return id;
  },

  patchAssistantMessage: (id, patch) => {
    set((s) => ({
      messages: s.messages.map((m) => (m.id === id ? { ...m, ...patch } : m)),
    }));
  },

  appendAssistantChunk: (id, chunk) => {
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === id ? { ...m, content: m.content + chunk } : m,
      ),
    }));
  },

  appendToolStep: (id, step) => {
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === id ? { ...m, steps: [...(m.steps ?? []), step] } : m,
      ),
    }));
  },

  setSending: (sending) => set({ isSending: sending }),
  setError: (msg) => set({ errorMessage: msg, isSending: false }),
}));
